import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import type { InventoryHireListRow, InventoryItem } from '@court-sync/shared';
import { TierFeatureKey, isLowStock, type Region } from '@court-sync/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { ClubFeatureGate } from '@/components/club/ClubFeatureGate';
import { InventoryItemDrawer } from '@/components/club/InventoryItemDrawer';
import { InventoryHireDrawer } from '@/components/club/InventoryHireDrawer';
import { InventoryStockAdjustModal } from '@/components/club/InventoryStockAdjustModal';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { clubInventoryService } from '@/services/clubInventory';
import { useAuthStore } from '@/store/auth';
import { getCurrencyFromRegion } from '@/utils/currency';
import { formatCurrency, type Currency } from '@/utils/currency';
import { ClipboardList, MoreVertical, Package, Pencil, Plus, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTour } from '@/components/ui/Tour';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { CLUB_PAGE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  CLUB_INVENTORY_PAGE_HELP,
  CLUB_INVENTORY_TOUR_KEY,
  getClubInventoryTabHelp,
  scheduleClubInventoryTourStart,
} from '@/lib/tours/clubInventoryTour';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';

type InventoryTab = 'items' | 'hires';

const INVENTORY_TABS: {
  id: InventoryTab;
  label: string;
  mobileLabel: string;
  tabId?: string;
}[] = [
  {
    id: 'items',
    label: 'Items',
    mobileLabel: 'Items',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_TAB_ITEMS,
  },
  {
    id: 'hires',
    label: 'Hires',
    mobileLabel: 'Hires',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_TAB_HIRES,
  },
];

function formatHireWhen(hire: InventoryHireListRow): string {
  const start = hire.startAt instanceof Date ? hire.startAt : new Date(hire.startAt);
  const end = hire.endAt instanceof Date ? hire.endAt : new Date(hire.endAt);
  return `${format(start, 'd MMM yyyy HH:mm')} – ${format(end, 'HH:mm')}`;
}

function InventoryHireHirerCell({ hire }: { hire: InventoryHireListRow }) {
  if (hire.hirerKind === 'member' && hire.hirerMemberId && hire.hirerDisplayName) {
    return (
      <div>
        <Link
          to={`/admin/members?member=${hire.hirerMemberId}`}
          className="font-medium text-primary hover:underline"
        >
          {hire.hirerDisplayName}
        </Link>
        {hire.hirerPhone ? (
          <p className="text-sm text-muted-foreground">
            <a href={`tel:${hire.hirerPhone}`} className="hover:underline">
              {hire.hirerPhone}
            </a>
          </p>
        ) : null}
      </div>
    );
  }

  if (hire.hirerKind === 'guest' && hire.hirerDisplayName) {
    return (
      <div>
        <p className="font-medium text-gray-900 dark:text-white">{hire.hirerDisplayName}</p>
        {hire.hirerPhone ? (
          <p className="text-sm text-muted-foreground">
            <a href={`tel:${hire.hirerPhone}`} className="hover:underline">
              {hire.hirerPhone}
            </a>
          </p>
        ) : null}
      </div>
    );
  }

  if (hire.hirerDisplayName) {
    return <span className="text-gray-900 dark:text-white">{hire.hirerDisplayName}</span>;
  }

  return <span className="text-muted-foreground">—</span>;
}

function itemTypeLabel(item: InventoryItem): string {
  if (item.isHireable && item.tracksStock) return 'Hire + stock';
  if (item.isHireable) return 'Hireable';
  if (item.tracksStock) return 'Stock';
  return '—';
}

export default function ClubInventory() {
  const { currentClub } = useCurrentClub();
  const { region, user } = useAuthStore();
  const { startPageTour, startTourWithCompletionKey } = usePageTourActions();
  const { isActive: isTourActive } = useTour();
  const billingRegion = (region === 'AU' ? 'AU' : 'US') as Region;
  const currency = getCurrencyFromRegion(billingRegion);

  const [activeTab, setActiveTab] = useState<InventoryTab>('items');
  const setActiveTabRef = useRef<(tab: InventoryTab) => void>(() => {});
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [hires, setHires] = useState<InventoryHireListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemDrawerOpen, setItemDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const itemCreateTourScheduledRef = useRef(false);
  const cancelItemCreateTourScheduleRef = useRef<(() => void) | null>(null);
  const [hireDrawerOpen, setHireDrawerOpen] = useState(false);
  const [stockItem, setStockItem] = useState<InventoryItem | null>(null);

  const itemNameById = useMemo(
    () => Object.fromEntries(items.map((item) => [item.id, item.name] as const)),
    [items]
  );

  const loadData = useCallback(async () => {
    if (!currentClub) return;
    setLoading(true);
    try {
      const [itemsRes, hiresRes] = await Promise.all([
        clubInventoryService.listItems(currentClub.id),
        clubInventoryService.listHires(currentClub.id),
      ]);
      setItems(itemsRes.success && itemsRes.data ? itemsRes.data : []);
      setHires(hiresRes.success && hiresRes.data ? hiresRes.data : []);
    } finally {
      setLoading(false);
    }
  }, [currentClub]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    setActiveTabRef.current = setActiveTab;
  }, []);

  const openCreateItem = useCallback(() => {
    setEditingItem(null);
    setItemDrawerOpen(true);
  }, []);

  const closeItemDrawer = useCallback(() => {
    setItemDrawerOpen(false);
    setEditingItem(null);
  }, []);

  const openEditItem = (item: InventoryItem) => {
    setEditingItem(item);
    setItemDrawerOpen(true);
  };

  const handleDeactivateItem = async (item: InventoryItem) => {
    if (!currentClub) return;
    if (!window.confirm(`Deactivate "${item.name}"?`)) return;
    const res = await clubInventoryService.deactivateItem(currentClub.id, item.id);
    if (res.success) {
      toast.success('Item deactivated');
      await loadData();
    } else {
      toast.error(res.error || 'Failed to deactivate item');
    }
  };

  const handleReturnHire = async (hire: InventoryHireListRow) => {
    if (!currentClub) return;
    const res = await clubInventoryService.returnHire(currentClub.id, hire.id);
    if (res.success) {
      toast.success('Marked as returned');
      await loadData();
    } else {
      toast.error(res.error || 'Failed to mark returned');
    }
  };

  const activeItems = items.filter((item) => item.isActive);

  const launchInventoryTour = useCallback(() => {
    return scheduleClubInventoryTourStart(startTourWithCompletionKey, {
      onEnterItems: () => setActiveTabRef.current('items'),
      onEnterHires: () => setActiveTabRef.current('hires'),
      onOpenCreateItem: openCreateItem,
      onCloseCreateItem: closeItemDrawer,
      onFinish: () => {
        closeItemDrawer();
        setActiveTabRef.current('items');
      },
    });
  }, [closeItemDrawer, openCreateItem, startTourWithCompletionKey]);

  useAutoPageTour(CLUB_INVENTORY_TOUR_KEY, Boolean(currentClub) && !loading, {
    onStart: launchInventoryTour,
    afterAnimationMs: 0,
  });

  usePageTourReady(
    'club-inventory-item-create',
    itemDrawerOpen && !editingItem && Boolean(currentClub),
  );

  const handleItemCreateDrawerPanelOpen = useCallback(() => {
    if (editingItem || itemCreateTourScheduledRef.current || isTourActive) return;
    if (!isPageTourComplete(user?.preferences, CLUB_INVENTORY_TOUR_KEY)) return;
    if (isPageTourComplete(user?.preferences, 'club-inventory-item-create')) return;
    itemCreateTourScheduledRef.current = true;
    cancelItemCreateTourScheduleRef.current?.();
    cancelItemCreateTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'club-inventory-item-create',
      user?.preferences,
    );
  }, [editingItem, isTourActive, startPageTour, user?.preferences]);

  useEffect(() => {
    if (!itemDrawerOpen) {
      itemCreateTourScheduledRef.current = false;
      cancelItemCreateTourScheduleRef.current?.();
      cancelItemCreateTourScheduleRef.current = null;
    }
  }, [itemDrawerOpen]);

  const inventoryTabHelp = useMemo(() => getClubInventoryTabHelp(activeTab), [activeTab]);

  return (
    <ClubFeatureGate feature={TierFeatureKey.INVENTORY_HIRE}>
      <div className="contents">
        <PageHeader
          title="Inventory"
          subtitle="Track stock and equipment hires"
          breadcrumbs={[{ label: 'Inventory', href: '/admin/inventory' }]}
          help={
            <PageFeatureHelp
              title={CLUB_INVENTORY_PAGE_HELP.title}
              tours={[
                {
                  tourKey: CLUB_INVENTORY_TOUR_KEY,
                  label: CLUB_INVENTORY_PAGE_HELP.tourLabel,
                  description: CLUB_INVENTORY_PAGE_HELP.tourDescription,
                  onStart: launchInventoryTour,
                },
              ]}
            >
              <p>{CLUB_INVENTORY_PAGE_HELP.description}</p>
              <p className="mt-2 text-muted-foreground">{inventoryTabHelp.description}</p>
            </PageFeatureHelp>
          }
          helpAnchorId={CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_GUIDE}
          actions={
            activeTab === 'items' ? (
              <Button id={CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_ITEMS_NEW} onClick={openCreateItem}>
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                {activeItems.length === 0 ? 'Add your first item' : 'Add item'}
              </Button>
            ) : activeTab === 'hires' ? (
              <Button onClick={() => setHireDrawerOpen(true)}>
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                Log hire
              </Button>
            ) : undefined
          }
        />

        <div id={CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_TABS}>
          <DashboardTabNav<InventoryTab>
            tabs={INVENTORY_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
            ariaLabel="Inventory sections"
          />
        </div>

        {loading && activeTab === 'items' ? (
          <div
            id={CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_ITEMS}
            className="flex min-h-[160px] items-center justify-center"
            aria-busy="true"
          >
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
          </div>
        ) : loading ? (
          <div className="flex min-h-[160px] items-center justify-center" aria-busy="true">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
          </div>
        ) : activeTab === 'items' ? (
          activeItems.length === 0 ? (
            <DashboardEmptyStateCard>
              <DashboardEmptyStateIcon icon={Package} />
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No inventory items</h3>
              <p className="mb-4 text-gray-500 dark:text-gray-400">
                Track balls, grips, and hireable equipment like ball machines.
              </p>
              <Button onClick={openCreateItem}>
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                Add your first item
              </Button>
            </DashboardEmptyStateCard>
          ) : (
            <DashboardDataCard>
              <div id={CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_ITEMS}>
              <Table>
                <TableHeader>
                  <TableRow className={dashboardTableHeaderRowClass}>
                    <TableHead className={dashboardTableHeadClass}>Name</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Type</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Qty</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Hire / hr</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Status</TableHead>
                    <TableHead className={dashboardTableHeadClass} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeItems.map((item) => (
                    <TableRow key={item.id} className={dashboardTableDataRowClass}>
                      <TableCell className={dashboardTableCellClass}>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.category ? (
                            <p className="text-xs text-muted-foreground">{item.category}</p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>{itemTypeLabel(item)}</TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        <div className="flex items-center gap-2">
                          <span>{item.quantity}</span>
                          {isLowStock(item.quantity, item.lowStockThreshold, item.tracksStock) ? (
                            <Badge variant="destructive" className="text-xs">
                              Low
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {item.isHireable && item.hirePriceCents != null
                          ? formatCurrency(item.hirePriceCents / 100, item.currency as Currency)
                          : '—'}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Item actions">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditItem(item)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            {item.tracksStock ? (
                              <DropdownMenuItem onClick={() => setStockItem(item)}>
                                Adjust stock
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => void handleDeactivateItem(item)}
                            >
                              Deactivate
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </DashboardDataCard>
          )
        ) : hires.length === 0 ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={ClipboardList} />
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No hires yet</h3>
            <p className="mb-4 text-gray-500 dark:text-gray-400">
              Walk-up loans and court booking add-ons appear here.
            </p>
            <Button onClick={() => setHireDrawerOpen(true)}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              Log a hire
            </Button>
          </DashboardEmptyStateCard>
        ) : (
          <DashboardDataCard>
            <div id={CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_HIRES}>
            <Table>
              <TableHeader>
                <TableRow className={dashboardTableHeaderRowClass}>
                  <TableHead className={dashboardTableHeadClass}>Item</TableHead>
                  <TableHead className={dashboardTableHeadClass}>Hirer</TableHead>
                  <TableHead className={dashboardTableHeadClass}>When</TableHead>
                  <TableHead className={dashboardTableHeadClass}>Qty</TableHead>
                  <TableHead className={dashboardTableHeadClass}>Status</TableHead>
                  <TableHead className={dashboardTableHeadClass} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {hires.map((hire) => (
                  <TableRow key={hire.id} className={dashboardTableDataRowClass}>
                    <TableCell className={dashboardTableCellClass}>
                      {itemNameById[hire.itemId] ?? 'Unknown item'}
                    </TableCell>
                    <TableCell className={dashboardTableCellClass}>
                      <InventoryHireHirerCell hire={hire} />
                    </TableCell>
                    <TableCell className={dashboardTableCellClass}>{formatHireWhen(hire)}</TableCell>
                    <TableCell className={dashboardTableCellClass}>{hire.quantity}</TableCell>
                    <TableCell className={dashboardTableCellClass}>
                      {hire.returnedAt ? 'Returned' : hire.status}
                    </TableCell>
                    <TableCell className={dashboardTableCellClass}>
                      {!hire.returnedAt &&
                      hire.status !== 'cancelled' &&
                      !hire.courtHireBookingId ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void handleReturnHire(hire)}
                        >
                          <RotateCcw className="mr-1 h-3.5 w-3.5" />
                          Return
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </DashboardDataCard>
        )}

        {currentClub ? (
          <>
            <InventoryItemDrawer
              clubId={currentClub.id}
              currency={currency}
              item={editingItem}
              isOpen={itemDrawerOpen}
              onClose={closeItemDrawer}
              onCreatePanelOpen={
                !editingItem && !isTourActive ? handleItemCreateDrawerPanelOpen : undefined
              }
              showCreateTourHelp={!editingItem && !isTourActive}
              showBackdrop={!isTourActive}
              onSaved={() => void loadData()}
            />
            <InventoryHireDrawer
              clubId={currentClub.id}
              items={activeItems}
              isOpen={hireDrawerOpen}
              onClose={() => setHireDrawerOpen(false)}
              onSaved={() => void loadData()}
            />
            <InventoryStockAdjustModal
              clubId={currentClub.id}
              item={stockItem}
              isOpen={stockItem != null}
              onClose={() => setStockItem(null)}
              onSaved={() => void loadData()}
            />
          </>
        ) : null}
      </div>
    </ClubFeatureGate>
  );
}
